import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Alert, Box, Button, IconButton, LinearProgress, Snackbar } from "@mui/material";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import moment from "moment";
import bkstApi from "../api";
import { getCustomerName, getQueryParam } from "../common/util";
import OrderPrintVersion from "../order/component/OrderPrintVersion";
import { bkeStRedirectUrl as bkeStRedirectUrlFromPath } from "../common/bkeStRedirect";

const VALID_LAYOUTS = new Set(["compact", "expanded"]);

function bkeStRedirectUrl() {
  return bkeStRedirectUrlFromPath(window.location.pathname, window.location.search);
}

export default function OrderPrintPage() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const layout = (searchParams.get("layout") || "").toLowerCase();
  const autoPrint = getQueryParam(window.location.search, "auto") === "1";

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [snackMsg, setSnackMsg] = useState("");

  useEffect(() => {
    if (order) {
      const title = `${getCustomerName(order.customer)} ${moment(order.fulfillment.date).format("MMM DD, YYYY")}`;
      document.title = title;
    }
  }, [order]);

  useEffect(() => {
    setLoading(true);
    setError("");
    bkstApi(`/my/order/${orderId}`)
      .then((res) => {
        setOrder(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError("Unable to load order.");
        setLoading(false);
      });
  }, [orderId]);

  useEffect(() => {
    if (!autoPrint || loading || error || !order || !VALID_LAYOUTS.has(layout)) {
      return undefined;
    }
    const timer = window.setTimeout(() => window.print(), 400);
    return () => window.clearTimeout(timer);
  }, [autoPrint, loading, error, order, layout]);

  const copyPageLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setSnackMsg("Link copied");
    } catch {
      setSnackMsg("Could not copy link");
    }
  };

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(`/order/${orderId}`);
    }
  };

  const sharePageLink = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ url, title: document.title });
      } else {
        await copyPageLink();
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        setSnackMsg("Could not share link");
      }
    }
  };

  if (loading) {
    return <LinearProgress />;
  }

  if (!VALID_LAYOUTS.has(layout)) {
    return (
      <Box p={2}>
        <Alert severity="error">Use layout=compact or layout=expanded.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#e8e8e8",
        py: { xs: 2, sm: 3 },
        px: { xs: 1.5, sm: 2 },
        "@media print": {
          bgcolor: "#fff",
          py: 0,
          px: 0,
        },
      }}
    >
      <Box
        className="order-print-toolbar"
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 1,
          width: "210mm",
          maxWidth: "100%",
          mx: "auto",
          mb: "1rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          "@media print": { display: "none" },
        }}
      >
        <IconButton aria-label="Close" onClick={goBack}>
          <CloseIcon />
        </IconButton>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton aria-label="Copy link" onClick={copyPageLink}>
            <ContentCopyOutlinedIcon />
          </IconButton>
          <IconButton aria-label="Share" onClick={sharePageLink}>
            <IosShareOutlinedIcon />
          </IconButton>
          <IconButton
            component="a"
            href={bkeStRedirectUrl()}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open in browser"
          >
            <OpenInNewOutlinedIcon />
          </IconButton>
          <IconButton aria-label="Print" onClick={() => window.print()}>
            <PrintOutlinedIcon />
          </IconButton>
        </Box>
      </Box>
      {error && (
        <Box p={2} sx={{ maxWidth: "210mm", mx: "auto" }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      {order && (
        <Box
          sx={{
            width: "210mm",
            maxWidth: "100%",
            minHeight: "297mm",
            mx: "auto",
            bgcolor: "#fff",
            boxShadow: "0 2px 12px rgba(0, 0, 0, 0.12)",
            boxSizing: "border-box",
            p: "0.25in",
            "@media print": {
              width: "100%",
              minHeight: "auto",
              boxShadow: "none",
              p: 0,
            },
          }}
        >
          <OrderPrintVersion type={layout} order={order} />
        </Box>
      )}
      <Box
        sx={{
          width: "210mm",
          maxWidth: "100%",
          mx: "auto",
          mt: 2,
          display: "flex",
          gap: 1,
          flexWrap: "wrap",
          justifyContent: "center",
          "@media print": { display: "none" },
        }}
      >
        <Button
          component="a"
          href="https://bakestreet.co/"
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          size="small"
        >
          bakestreet.co
        </Button>
        <Button
          component="a"
          href="https://www.google.com/"
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          size="small"
        >
          google.com
        </Button>
        <Button
          component="a"
          href={window.location.href}
          target="_blank"
          rel="noopener noreferrer"
          variant="outlined"
          size="small"
        >
          print page (link)
        </Button>
      </Box>
      <Snackbar
        open={Boolean(snackMsg)}
        autoHideDuration={2500}
        message={snackMsg}
        onClose={() => setSnackMsg("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
